import { Duration } from 'dayjs/plugin/duration.js';
import { kMillisecondsPerHour } from '../../utility/constants.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/dto.js';

export class PoolBookingSettings extends DataModel {
  static readonly TYPE_ID: string = '717eacfecc8c414fb42043625f4c72b0';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
        public readonly numberOfLanes: number,
        public readonly maxCapacityPerLane: number,
        public readonly bookingInAdvance: Duration,
  ) {
    super();
  }

  get $typeId(): string {
    return PoolBookingSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, PoolBookingSettings.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): PoolBookingSettings {
    return new PoolBookingSettings(
        MapUtils.get<number>(map, 'numberOfLanes'),
        MapUtils.get<number>(map, 'maxCapacityPerLane'),
        context.decodeDuration(MapUtils.get<number>(map, 'bookingInAdvance')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'numberOfLanes': this.numberOfLanes,
      'maxCapacityPerLane': this.maxCapacityPerLane,
      'bookingInAdvance': context.encodeDuration(this.bookingInAdvance),
    };
  }

  clone(): this {
    return new PoolBookingSettings(
        this.numberOfLanes,
        this.maxCapacityPerLane,
        TimeZone.cloneDuration(this.bookingInAdvance),
    ) as this;
  }

  isSame(other: PoolBookingSettings): boolean {
    if(this === other) {
      return true;
    }

    return other.numberOfLanes == this.numberOfLanes &&
            other.maxCapacityPerLane == this.maxCapacityPerLane &&
            other.bookingInAdvance == this.bookingInAdvance;
  }
}

export class PoolBookingSettingsBuilder extends DataModelBuilder<PoolBookingSettings> {
  build(context: SerializationContext, map: SerializedData): PoolBookingSettings {
    return PoolBookingSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(PoolBookingSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PoolBookingSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.set(map, 'bookingInAdvance', kMillisecondsPerHour * 4);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
